import IconSortDownSolid from '@/assets/icons/font_awesome/sort-down-solid.svg?react';
import IconSortUpSolid from '@/assets/icons/font_awesome/sort-up-solid.svg?react';
import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { use_ref_with_fallback } from '@/hooks/_base/use_ref_with_fallback';
import { TABLE__empty } from '@/pages/_base/table/TABLE__empty';
import { not_implemented_type } from '@shared/asserts/utils_asserts';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { DTODiffusionModelUDBase } from '@shared/dto/+model/sd/_base/dto_diffusion_model_ud_base';
import { DTODiffusionModelsTableBase } from '@shared/dto/+model/table_base/dto_diffusion_model_table_base';
import { DTODiffusionModelsTablePageSettingsBase } from '@shared/dto/+model/table_base/dto_diffusion_models_table_page_settings_base';
import { ENUM__sort_direction } from '@shared/dto/_database_entity/ENUM__sort_direction';
import { FIELD_OF_TYPE } from '@shared/types/types';
import { ColumnDef, flexRender, getCoreRowModel, getFilteredRowModel, Header, useReactTable } from '@tanstack/react-table';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import { useMemo, useState } from 'react';
import { TABLE__loading } from '../../_base/table/TABLE__loading';
import { DiffusionModelsTableRow } from './rows/diffusion_models_table_row';
import { DiffusionModelsTableRowLoading } from './rows/diffusion_models_table_row_loading';

interface SortingState {
  column_name: string;
  sort_direction: number; //ESortDirection
}

interface Props {
  current_table_page_settings: DTODiffusionModelsTablePageSettingsBase;
  current_table: DTODiffusionModelsTableBase;
  columns: ColumnDef<DTODiffusionModelUDBase>[];
  sort_keys: string[];
  stub_template: DTODiffusionModelUDBase;

  on_sort_updated: (column_name: string, sort_direction: number) => void;
  on_first_index_updated: (first_index: number) => void;
}

export function DiffusionModelsTableBase({
  current_table_page_settings,
  current_table,
  columns,
  sort_keys,
  stub_template,

  on_sort_updated,
  on_first_index_updated,
}: Props) {
  const ROW_HEIGHT = 70;

  const ref_table_container = use_ref_with_fallback<HTMLDivElement>(ENUM__html_ref_stub.DIV);

  const items = useMemo(() => get_models(), [current_table]);

  const [sort_value, set_sort_value] = useState<SortingState>({
    column_name: '',
    sort_direction: 0,
  });

  const virtualizer_count = useMemo(get_virtualizer_count, [
    current_table_page_settings.filter_type,
    current_table.items_amount_by_type_all,
    current_table.items_amount_by_type_map,
  ]);

  const table = useReactTable<DTODiffusionModelUDBase>({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const row_virtualizer = useVirtualizer({
    count: virtualizer_count,
    getScrollElement: () => ref_table_container.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
    onChange: instance => {
      if (!instance.range) return;

      let first_visible_index = instance.range.startIndex;

      on_first_index_updated(first_visible_index);
    },
  });

  /*
   * METHODS
   */

  function get_models() {
    const map_size = current_table.pages_map.size();

    let out: DTODiffusionModelUDBase[] = [];

    for (let index_item = 0; index_item <= map_size; index_item++) {
      const page = current_table.get_page_by_index(index_item);

      if (page.is_stub()) {
        for (let index_page = 0; index_page < CONSTANTS__shared.DIFFUSION_MODELS_TABLE_PAGE_ITEMS_AMOUNT; index_page++) {
          out.push(stub_template.clone());
        }
      } else {
        out.push(...page.items);
      }
    }

    return out;
  }

  /*
   * EVENTS
   */

  function on_column_header_clicked(header: Header<DTODiffusionModelUDBase, unknown>) {
    const header_column = header.getContext();
    const header_column_name = header_column.column.id as FIELD_OF_TYPE<DTODiffusionModelUDBase>;

    if (!sort_keys.includes(header_column_name)) return;

    let sort_direction;

    let column_name = '';

    if (sort_value.column_name !== header_column_name) {
      column_name = header_column_name;
      sort_direction = ENUM__sort_direction.A_TO_Z;
    } else {
      switch (sort_value.sort_direction) {
        case ENUM__sort_direction.A_TO_Z:
          sort_direction = ENUM__sort_direction.Z_TO_A;
          column_name = header_column_name;
          break;
        case ENUM__sort_direction.Z_TO_A:
          sort_direction = ENUM__sort_direction.NONE;
          break;
        default:
          not_implemented_type(sort_value.sort_direction);
      }
    }

    set_sort_value({ column_name, sort_direction });

    on_sort_updated(column_name, sort_direction);
  }

  /*
   * METHODS
   */

  function get_virtualizer_count() {
    return current_table_page_settings.is_filter_type_all()
      ? current_table.items_amount_by_type_all
      : (current_table.items_amount_by_type_map.get(current_table_page_settings.filter_type) ?? 0);
  }

  /*
   * RENDERS
   */

  function render_icon_sort(header: Header<DTODiffusionModelUDBase, unknown>, sort_value: SortingState) {
    const is_sorted = header.getContext().column.id === sort_value.column_name;

    if (!is_sorted) return null;

    let class_name = '!w-4 !h-4 !text-component-item-foreground';

    return (
      <span className="ml-[4px]">
        {sort_value.sort_direction === ENUM__sort_direction.A_TO_Z ? <IconSortUpSolid className={class_name} /> : <IconSortDownSolid className={class_name} />}
      </span>
    );
  }

  function render_row_loading(virtual_row: VirtualItem) {
    return (
      <DiffusionModelsTableRowLoading
        key={`loading_${virtual_row.index}_row`}
        style={{ height: `${ROW_HEIGHT}px`, transform: `translateY(${virtual_row.start}px)` }}
        className="w-full top-0 left-0 absolute"
      />
    );
  }

  function render_table_rows() {
    return (
      <>
        {row_virtualizer.getVirtualItems().map(virtual_row => {
          const row = table.getRowModel().rows[virtual_row.index];

          if (!row || row.original.is_stub()) return render_row_loading(virtual_row);

          return (
            <DiffusionModelsTableRow
              key={row.id}
              row={row}
              style={{ height: `${ROW_HEIGHT}px`, transform: `translateY(${virtual_row.start}px)` }}
              className="w-full top-0 left-0 absolute"
            />
          );
        })}
      </>
    );
  }

  function render_table_content() {
    if (current_table.is_stub()) return <TABLE__loading />;

    return virtualizer_count === 0 ? <TABLE__empty /> : render_table_rows();
  }

  /*
   * HTML
   */

  return (
    <div className="flex flex-col h-full">
      <div className="border-b-2 border-dashed border-component-border flex">
        {table.getFlatHeaders().map((header, idx) => (
          <div
            key={header.id}
            style={{
              width: header.getSize(),
              userSelect: 'none',
              borderRight: idx < table.getFlatHeaders().length - 1 ? '2px dashed ' : 'none',
            }}
            className={`flex items-center justify-start cursor-pointer p-[10px] !border-r-component-border`}
            onClick={() => on_column_header_clicked(header)}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
            {render_icon_sort(header, sort_value)}
          </div>
        ))}
      </div>

      <div ref={ref_table_container} className="relative overflow-auto h-full">
        <div style={{ height: `${row_virtualizer.getTotalSize()}px` }} className="relative w-full">
          {render_table_content()}
        </div>
      </div>
    </div>
  );
}
